import React, { useState, useMemo, useEffect } from "react";
import {
    Box,
    TextField,
    Typography,
    FormControl,
    Select,
    MenuItem,
    GlobalStyles,
    useTheme,
    Chip,
    useMediaQuery,
    CircularProgress
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { frFR, enUS } from "@mui/x-data-grid/locales";
import { useDispatch, useSelector } from "react-redux";
import { 
    getSubscriptionsHistory 
} from "../../Redux/Reducers/SubscriptionsReducer";
import { useTranslation } from "react-i18next";
import { formatUTCDateForDataGrid } from "../../utils/dateUtils";



function CustomLoadingOverlay() {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '150px' }}>
            <CircularProgress sx={{ color: '#FF69B4' }} />
        </Box>
    );
}

function SubscriptionsHistoryManagement() {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();

    const { histories = [], loading, pagination } = useSelector((state) => state.subscriptions.subscriptionHistory || {});
    
    const [searchText, setSearchText] = useState("");
    const [statusFilter, setStatusFilter] = useState("");

    // For Table Thead Width
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('xl'));
    const isLarge = useMediaQuery(theme.breakpoints.up('xl'));

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });
    
    const STATUS_OPTIONS = [
        { label: t('subscriptions.history.status_types.active',"ACTIVE"), value: "ACTIVE" },
        { label: t('subscriptions.history.status_types.expired',"EXPIRED"), value: "EXPIRED" },
        { label: t('subscriptions.history.status_types.cancelled',"CANCELLED"), value: "CANCELLED" },
        { label: t('subscriptions.history.status_types.pending',"PENDING"), value: "PENDING" },
    ];

    useEffect(() => {
        dispatch(
          getSubscriptionsHistory({
            page: paginationModel.page + 1,
            limit: paginationModel.pageSize,
            search: searchText,
            status: statusFilter 
          })
        );
    }, [paginationModel, statusFilter, searchText, dispatch]);

    const formatKey = (key) => {
        return key
            .replace(/([A-Z])/g, " $1")   // add space before capital letters
            .replace(/^./, (str) => str.toUpperCase()); // capitalize first letter
    };

    const columns = [
        {
            field: 'sno',
            headerName: t('sno', 'S.No'),
            width: 80,
            sortable: false,
            renderCell: (params) => {
                return params.row?.sno;
            }
        },
        { 
            field: "customer_name", 
            headerName: t('subscriptions.history.fields.customer_details',"Customer Details"), 
            ...(isMobile && { width: 200 }),
            ...(isLarge && { flex: 1 }),
            renderCell: (params) => {
                return params.row?.customer?.name ?? params.row?.customer?.email;
            }
        },
        { 
            field: "plan_details", 
            headerName: t('subscriptions.history.fields.plan_details',"Plan Details"), 
            ...(isMobile && { width: 150 }),
            ...(isLarge && { flex: 1 }),
            renderCell: (params) => {
                const features = params.row?.plan?.features;
                return (
                    <Box>
                        <Typography variant="body2">{params.row?.plan?.name}</Typography>
                            
                        {Object.entries(features).map(([key, value]) => {
                            let displayValue = value;

                            if (typeof value === "string") {
                                displayValue = value.charAt(0).toUpperCase() + value.slice(1);
                            } else if (typeof value === "boolean") {
                                displayValue = value ? "True" : "False";
                            }

                            return (
                                <Typography key={key} variant="body2">
                                    {formatKey(key)} : {displayValue + (key === "maxStreamPerDay" && value !== "unlimited" ? " Mins" : "")}
                                </Typography>
                            );
                        })}       
                    </Box>
                );
            }
        },
        { 
            field: "startsAt", 
            headerName: t('subscriptions.history.fields.startsAt',"Started At"), 
            ...(isMobile && { width: 150 }),
            ...(isLarge && { flex: 1 }),
            valueFormatter: (value) => {
                return formatUTCDateForDataGrid(value);
            }
        },
        { 
            field: "endsAt", 
            headerName: t('subscriptions.history.fields.endsAt',"Ends At"), 
            ...(isMobile && { width: 150 }),
            ...(isLarge && { flex: 1 }),
            valueFormatter: (value) => {
                return formatUTCDateForDataGrid(value);
            }
        },
        { 
            field: "createdAt", 
            headerName: t('subscriptions.history.fields.createdAt',"Created At"), 
            ...(isMobile && { width: 150 }),
            ...(isLarge && { flex: 1 }),
            valueFormatter: (value) => {
                return formatUTCDateForDataGrid(value);
            }
        },
        {
            field: "status",
            headerName: t('subscriptions.history.fields.status', "Status"),
            ...(isMobile && { width: 150 }),
            ...(isLarge && { flex: 1 }),
            // renderCell: (params) => {
            //     return (params.value);
            // }
            renderCell: (params) => (
                <Chip
                    label={params.value}
                    color={params.value ? "success" : "error"}
                    size="small"
                    variant="outlined"
                />
            )
        },
    ];

    const rows = useMemo(() => {
        if (!Array.isArray(histories) || histories.length === 0) {
            return [];
        }
        
        return histories.map((data, index) => {
            if (!data || !data.id) {
                console.warn('Invalid data at index', index, data);
                return null;
            }
            
            return {
                id: data.id,
                backendId: data.id,
                sno: paginationModel.page * paginationModel.pageSize + index + 1,
                createdAt: data.createdAt || null,
                status: data.status || false,
                startsAt: data.startsAt || null,
                endsAt: data.endsAt || null,
                note: data.note || null,
                customer: data.customer || null,
                source: data.source || null,
                plan: data.plan || null,
            };
        }).filter(Boolean);
    }, [histories, paginationModel]);
  
    return (
        <Box sx={{ p: '36px 24px 24px' }}>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: '24px', flexWrap: 'wrap', columnGap: '6px', rowGap: '16px' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', lineHeight: '32px' }} className='main_title'>{t('subscriptions.history.title', 'Subscription History')}
                </Typography>

                <Box display="flex" gap={2}>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select 
                            displayEmpty
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)} 
                            sx={{
                                color: "#fff",
                                fontWeight: 400,
                                borderRadius: '999px',
                                backgroundColor: 'rgba(230, 210, 249, 0.2)',
                                height: '38px',
                                "& .MuiSelect-select": {
                                    padding: "12px 20px",
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                    border: "1px solid rgba(230, 210, 249, 0.1) !important",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                    border: "1px solid rgba(230, 210, 249, 0.1)",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    border: "1px solid rgba(230, 210, 249, 0.1)",
                                },
                                "& .MuiSvgIcon-root": {
                                    color: "#fff",
                                },
                            }}
                        >
                            <MenuItem value="">{t('subscriptions.history.all', "All")}</MenuItem>
                            {STATUS_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    
                    <TextField
                    size="small"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    placeholder={t('search', 'Search')}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '999px',
                            backgroundColor: 'rgba(230, 210, 249, 0.2)',
                            color: '#fff',
                            height: '38px',
                            '& fieldset': {
                                borderColor: 'rgba(230, 210, 249, 0.1)',
                            },
                            '&:hover fieldset': {
                                borderColor: 'rgba(230, 210, 249, 0.1)',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: 'rgba(230, 210, 249, 0.1)',
                                borderWidth: '1px',
                            },
                        },
                        '& .MuiOutlinedInput-input': {
                            padding: '10px 16px',
                            color: '#FF69B4',
                            fontSize: '15px',
                            lineHeight: '18px',
                            
                        },
                        '& input::placeholder': {
                            color: 'rgba(255,255,255,0.6)',
                            opacity: 1,
                        },
                        '& .MuiFormHelperText-root': {
                            ml: '20px'
                        }
                    }}
                    />
                </Box>
            </Box>

            {/* Table */}
            <Box sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(230, 210, 249, 0.2)', borderRadius: '10px', px: '12px' }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    slots={{ toolbar: GridToolbar, loadingOverlay: CustomLoadingOverlay }}
                    paginationMode="server" 
                    rowCount={pagination?.total || 0}
                    paginationModel={paginationModel}
                    onPaginationModelChange={(model) => {
                        setPaginationModel(model);
                    }}
                    slotProps={{
                        toolbar: {
                            csvOptions: { disableToolbarButton: true },
                            printOptions: { disableToolbarButton: true },
                        },
                    }}
                    initialState={{
                    pagination: { paginationModel: { pageSize: 10 } },
                        sorting: { sortModel: [{ field: "name", sort: "asc" }] }
                    }}
                    getRowId={(row) => row.id || row.sno}
                    pageSizeOptions={[10, 25, 50, 100]}
                    disableRowSelectionOnClick
                    localeText={
                        i18n.language?.toLowerCase().startsWith("fr")
                            ? frFR.components.MuiDataGrid.defaultProps.localeText
                            : enUS.components.MuiDataGrid.defaultProps.localeText
                    }
                    sx={{ 
                        backgroundColor: 'transparent', 
                        border: 'none', 
                        borderRadius: '0', 
                        '& .MuiDataGrid-toolbarContainer': {
                            padding: '12px',
                            margin: '0 -12px',
                            gap: '10px',
                            backgroundColor: 'rgba(255,255,255,0.05)',
                        },
                        '& .MuiDataGrid-topContainer::after': {
                            display: 'none',
                        },
                        '& .MuiDataGrid-toolbarContainer .MuiButton-root': {
                            color: '#fff',
                            border: '1px solid rgba(230, 210, 249, 0.2)',
                            borderRadius: '999px',
                            textTransform: 'none',
                            fontWeight: 500,
                            padding: '6px 12px',
                            backgroundColor: 'rgba(255,255,255,0.05)',
                        },
                        '& .MuiDataGrid-toolbarContainer .MuiButton-root .MuiButton-icon .MuiBadge-root svg': {
                            width: '18px',
                            height: '18px',
                        },
                        '& .MuiDataGrid-virtualScrollerContent': {
                            maxHeight: 'auto',
                            height: 'auto !important'
                        },
                        '& .MuiDataGrid-virtualScroller': {
                            borderRadius: '0 !important',
                        },
                        '& .MuiDataGrid-virtualScrollerRenderZone': {
                            position: 'inherit',
                        },
                        '& .MuiDataGrid-virtualScrollerRenderZone>.MuiDataGrid-row': {
                            maxHeight: 'none !important',
                            height: 'auto !important',
                        },
                        '& .MuiDataGrid-cell': {
                            height: 'auto !important',
                            minHeight: 'var(--height)',
                            paddingTop: '5px',
                            paddingBottom: '5px',
                        },
                        '& .MuiDataGrid-cell>.MuiBox-root': {
                            minHeight: '100%',
                            alignItems: 'center',
                        },
                        '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-cell': {
                            borderRadius: '0 !important',
                            borderColor: 'rgba(230, 210, 249, 0.2) !important',
                            color: '#fff',
                        },
                        '& .MuiDataGrid-columnHeaders [role=row]': {
                            backgroundColor: '#1a1a1a',
                        },
                        '& .MuiDataGrid-footerContainer': {
                            borderColor: 'rgba(230, 210, 249, 0.2) !important'
                        },
                        '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader': {
                            color: 'white',
                        },
                        '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                            outline: 'none',
                        },
                        '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                            opacity: '0.8 !important',
                            fill: 'white',
                        },
                        '& .MuiDataGrid-cell .MuiChip-outlined': {
                            border: '0 !important',
                            height: 'auto',
                            fontWeight: '600',
                        },
                        '& .MuiDataGrid-cell .MuiChip-outlined.MuiChip-colorSuccess': {
                            color: "#03c11c",
                        },
                        '& .MuiTablePagination-root': {
                            color: '#fff'
                        },
                        '& .MuiTablePagination-selectLabel': {
                            marginBottom: '0',
                        },
                        '& .MuiTablePagination-input': {
                            marginRight: '10px',
                        },
                        '& .MuiTablePagination-input .MuiSelect-select': {
                            paddingTop: '2px',
                            paddingBottom: '2px',
                            border: '1px solid rgba(230, 210, 249, 0.2)',
                            backgroundColor: 'rgba(255,255,255,0.05)',
                            borderRadius: '4px',
                        },
                        '& .MuiTablePagination-input .MuiSvgIcon-root': {
                            fill: 'white'
                        },
                        '& .MuiTablePagination-toolbar .MuiTablePagination-displayedRows': {
                            marginBottom: '0',
                        },
                        '& .MuiTablePagination-actions': {
                            marginLeft: '10px !important',
                        },
                        '& .MuiTablePagination-actions .MuiButtonBase-root': {
                            color: '#FF69B4',
                        },
                        '& .MuiDataGrid-filler': {
                            height: 'auto !important',
                        },
                        '& .MuiDataGrid-filler>div': {
                            borderColor: 'transparent',
                        },
                        '& .MuiDataGrid-overlayWrapper': {
                            minHeight: '120px',
                        },
                        '& .MuiDataGrid-overlayWrapperInner': {
                            minHeight: '120px',
                        },
                        '& .MuiDataGrid-overlay': {
                            color: '#fff',
                            borderTop: '1px solid rgba(230, 210, 249, 0.2)',
                            backgroundColor: 'transparent',
                            minHeight: '120px',
                        },
                        '& .MuiDataGrid-main .MuiDataGrid-virtualScroller .MuiDataGrid-virtualScrollerContent .MuiDataGrid-row .MuiDataGrid-cell': {
                            display: 'flex',
                            alignItems: 'center',
                        }
                    }}
                />
            </Box>

            <GlobalStyles
                styles={{
                    '.MuiDataGrid-panel .MuiDataGrid-paper,.MuiPopover-root .MuiPopover-paper, .base-Popper-root .MuiPaper-root, .MuiDialog-root .MuiPaper-root': {
                    backgroundColor: '#252525 !important',
                    border: '1px solid rgba(230, 210, 249, 0.1)',
                    fontSize: '15px',
                    color: '#fff',
                    borderRadius: '4px',
                    },
                    '.MuiDialog-root .MuiPaper-root': {
                    borderRadius: '20px',
                    },
                    '.MuiDataGrid-columnsManagementHeader': {
                    padding: '10px !important',
                    },
                    '.MuiInputBase-formControl': {
                    fontSize: '14px !important',
                    color: 'white !important',
                    borderRadius: '999px !important',
                    },
                    '.MuiInputBase-formControl .MuiOutlinedInput-notchedOutline': {
                    border: '1px solid rgba(230, 210, 249, 0.1) !important',
                    },
                    '.MuiInputBase-formControl .MuiInputAdornment-positionStart svg': {
                    fill: 'white',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter': {
                    borderTop: '1px solid rgba(230, 210, 249, 0.1)',
                    padding: '4px 8px 4px 16px',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagement':{
                    padding: '0px 16px 12px',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagement .MuiFormControlLabel-root .MuiFormControlLabel-label,.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiTypography-root': {
                    fontSize: '14px',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagement .MuiFormControlLabel-root .MuiCheckbox-root .MuiSvgIcon-root, .MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiSvgIcon-root': {
                    fill: 'white',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiButtonBase-root': {
                    color: '#FF69B4',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiDataGrid-filterFormDeleteIcon .MuiSvgIcon-root,.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root svg,.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiListItemIcon-root .MuiSvgIcon-root': {
                    fill: 'white',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiFormLabel-root': {
                    color: 'white',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root:before': {
                    borderColor: 'rgba(230, 210, 249, 0.1) !important',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root:after': {
                    display: 'none',
                    },
                    '.MuiPopover-root .MuiPopover-paper .MuiList-root, .base-Popper-root .MuiPaper-root .MuiList-root': {
                    padding: '0',
                    },
                    '.MuiPopover-root .MuiPopover-paper .MuiList-root .MuiButtonBase-root, .base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root, .base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiTypography-root': {
                    fontSize: '14px',
                    },
                    '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root': {
                    padding: '5px 10px',
                    },
                    '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root.Mui-selected .MuiListItemIcon-root .MuiSvgIcon-root': {
                    fill: '#FF69B4',
                    },
                    '.base-Popper-root .MuiPaper-root .MuiDivider-root': {
                    borderColor: 'rgba(230, 210, 249, 0.3) !important',
                    margin: '0',
                    },
                    '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiListItemIcon-root .MuiSvgIcon-root': {
                        minWidth: '32px',
                    },
                    '.MuiDataGrid-scrollbarFiller': {
                        borderTop: 'none !important',
                    },
                    '& .MuiDataGrid-scrollbar': {
                        '&::-webkit-scrollbar': {
                            width: '8px',
                            height: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: 'rgba(255, 255, 255, 0.05)',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: 'rgba(230, 210, 249, 0.3)',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            background: 'rgba(230, 210, 249, 0.5)',
                        },
                    },
                }}
            />
        </Box>
    );
}

export default SubscriptionsHistoryManagement;
